import React from "react";
import { Link } from "react-router-dom";

/**
 * Visible breadcrumb trail. Emits matching BreadcrumbList JSON-LD
 * whenever `items` is a non-empty array of { name, href? } — see
 * `buildBreadcrumbSchema()` at the bottom for the schema helper.
 *
 * The last item is rendered as plain text (current page) and is NOT
 * a link, matching Google's guidance for BreadcrumbList.
 */
export default function Breadcrumbs({ items = [], className = "" }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className={`text-xs sm:text-sm text-neutral-500 ${className}`}
      data-testid="breadcrumbs"
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <li key={idx} className="flex items-center gap-1.5">
              {isLast || !item.href ? (
                <span
                  className={isLast ? "text-neutral-900" : ""}
                  aria-current={isLast ? "page" : undefined}
                  data-testid={`breadcrumb-item-${idx}`}
                >
                  {item.name}
                </span>
              ) : (
                <Link
                  to={item.href}
                  className="hover:text-neutral-900 transition-colors"
                  data-testid={`breadcrumb-item-${idx}`}
                >
                  {item.name}
                </Link>
              )}
              {!isLast && <span aria-hidden="true" className="text-neutral-300">/</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Build a schema.org BreadcrumbList object for the same items array.
 * The `origin` is required for absolute URLs (Google's guidance for
 * BreadcrumbList).
 */
export function buildBreadcrumbSchema(items, origin) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => {
      const el = {
        "@type": "ListItem",
        position: idx + 1,
        name: item.name,
      };
      if (item.href) {
        el.item = item.href.startsWith("http") ? item.href : `${origin}${item.href}`;
      }
      return el;
    }),
  };
}
